import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { Document, Packer, Paragraph, Table, TableCell, TableRow, TextRun, AlignmentType, WidthType } from 'docx';
import { format } from 'date-fns';

// Types for export configuration
export interface ExportConfig {
  title?: string;
  subtitle?: string;
  filename?: string;
  includeStats?: boolean;
  includeTimestamp?: boolean;
  organizationName?: string;
  organizationAddress?: string;
  organizationPhone?: string;
  organizationEmail?: string;
  logo?: string; // Base64 image data
}

export interface MemberExportData {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  membership_type?: string;
  status: string;
  created_at: string;
  playing_level?: string;
  jersey_number?: number;
  dob?: string;
  nationality?: string;
  residential_address?: string;
  occupation?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
}

export interface ExportStats {
  total: number;
  active: number;
  pending: number;
  suspended: number;
  disabled: number;
  expiring: number;
}

// Default export configuration
const defaultConfig: ExportConfig = {
  title: 'Member Directory',
  subtitle: 'Complete list of club members',
  filename: `members_export_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}`,
  includeStats: true,
  includeTimestamp: true,
  organizationName: 'OG Basketball Club',
  organizationAddress: 'Dar es Salaam, Tanzania',
  organizationPhone: '+255 XXX XXX XXX',
  organizationEmail: 'info@ogbasketball.com'
};

// CSV Export
export const exportToCSV = (data: MemberExportData[], config?: Partial<ExportConfig>) => {
  const finalConfig = { ...defaultConfig, ...config };
  
  // Prepare CSV headers
  const headers = [
    'ID',
    'First Name',
    'Last Name',
    'Email',
    'Phone',
    'Membership Type',
    'Status',
    'Playing Level',
    'Jersey Number',
    'Date of Birth',
    'Nationality',
    'Address',
    'Occupation',
    'Emergency Contact',
    'Emergency Phone',
    'Member Since'
  ];

  // Prepare CSV rows
  const rows = data.map(member => [
    member.id,
    member.first_name,
    member.last_name,
    member.email,
    member.phone,
    member.membership_type || '',
    member.status,
    member.playing_level || '',
    member.jersey_number || '',
    member.dob ? format(new Date(member.dob), 'yyyy-MM-dd') : '',
    member.nationality || '',
    member.residential_address || '',
    member.occupation || '',
    member.emergency_contact_name || '',
    member.emergency_contact_phone || '',
    member.created_at ? format(new Date(member.created_at), 'yyyy-MM-dd') : ''
  ]);

  // Create CSV content
  const csvContent = [
    // Add header information if requested
    ...(finalConfig.includeTimestamp ? [`# ${finalConfig.title} - Generated on ${format(new Date(), 'yyyy-MM-dd HH:mm:ss')}`] : []),
    ...(finalConfig.organizationName ? [`# ${finalConfig.organizationName}`] : []),
    '',
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
  ].join('\n');

  // Download the file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  saveAs(blob, `${finalConfig.filename}.csv`);
};

// Excel Export
export const exportToExcel = (data: MemberExportData[], stats?: ExportStats, config?: Partial<ExportConfig>) => {
  const finalConfig = { ...defaultConfig, ...config };
  
  // Create a new workbook
  const workbook = XLSX.utils.book_new();

  // Prepare main data
  const worksheetData = data.map(member => ({
    'ID': member.id,
    'First Name': member.first_name,
    'Last Name': member.last_name,
    'Email': member.email,
    'Phone': member.phone,
    'Membership Type': member.membership_type || '',
    'Status': member.status,
    'Playing Level': member.playing_level || '',
    'Jersey Number': member.jersey_number || '',
    'Date of Birth': member.dob ? format(new Date(member.dob), 'yyyy-MM-dd') : '',
    'Nationality': member.nationality || '',
    'Address': member.residential_address || '',
    'Occupation': member.occupation || '',
    'Emergency Contact': member.emergency_contact_name || '',
    'Emergency Phone': member.emergency_contact_phone || '',
    'Member Since': member.created_at ? format(new Date(member.created_at), 'yyyy-MM-dd') : ''
  }));

  // Create main worksheet
  const mainWorksheet = XLSX.utils.json_to_sheet(worksheetData);

  // Add column widths
  const columnWidths = [
    { wch: 8 },  // ID
    { wch: 15 }, // First Name
    { wch: 15 }, // Last Name
    { wch: 25 }, // Email
    { wch: 15 }, // Phone
    { wch: 20 }, // Membership Type
    { wch: 12 }, // Status
    { wch: 15 }, // Playing Level
    { wch: 12 }, // Jersey Number
    { wch: 12 }, // Date of Birth
    { wch: 15 }, // Nationality
    { wch: 30 }, // Address
    { wch: 20 }, // Occupation
    { wch: 20 }, // Emergency Contact
    { wch: 15 }, // Emergency Phone
    { wch: 12 }  // Member Since
  ];
  mainWorksheet['!cols'] = columnWidths;

  // Add main worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, mainWorksheet, 'Members');

  // Add statistics worksheet if stats are provided
  if (stats && finalConfig.includeStats) {
    const statsData = [
      { 'Metric': 'Total Members', 'Count': stats.total },
      { 'Metric': 'Active Members', 'Count': stats.active },
      { 'Metric': 'Pending Members', 'Count': stats.pending },
      { 'Metric': 'Suspended Members', 'Count': stats.suspended },
      { 'Metric': 'Disabled Members', 'Count': stats.disabled },
      { 'Metric': 'Expiring Soon', 'Count': stats.expiring }
    ];

    const statsWorksheet = XLSX.utils.json_to_sheet(statsData);
    statsWorksheet['!cols'] = [{ wch: 20 }, { wch: 15 }];
    XLSX.utils.book_append_sheet(workbook, statsWorksheet, 'Statistics');
  }

  // Add summary by status
  const statusSummary = data.reduce((acc, member) => {
    acc[member.status] = (acc[member.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const summaryData = Object.entries(statusSummary).map(([status, count]) => ({
    'Status': status.charAt(0).toUpperCase() + status.slice(1),
    'Count': count,
    'Percentage': `${((count / data.length) * 100).toFixed(1)}%`
  }));

  const summaryWorksheet = XLSX.utils.json_to_sheet(summaryData);
  summaryWorksheet['!cols'] = [{ wch: 15 }, { wch: 10 }, { wch: 12 }];
  XLSX.utils.book_append_sheet(workbook, summaryWorksheet, 'Summary');

  // Generate and download the file
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(blob, `${finalConfig.filename}.xlsx`);
};

// PDF Export
export const exportToPDF = (data: MemberExportData[], stats?: ExportStats, config?: Partial<ExportConfig>) => {
  const finalConfig = { ...defaultConfig, ...config };
  
  const doc = new jsPDF('l', 'mm', 'a4'); // Landscape orientation
  let yPos = 20;

  // Add header
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text(finalConfig.title || 'Member Directory', 20, yPos);
  yPos += 10;

  if (finalConfig.subtitle) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text(finalConfig.subtitle, 20, yPos);
    yPos += 8;
  }

  // Add organization info
  doc.setFontSize(10);
  if (finalConfig.organizationName) {
    doc.text(finalConfig.organizationName, 20, yPos);
    yPos += 5;
  }
  if (finalConfig.organizationAddress) {
    doc.text(finalConfig.organizationAddress, 20, yPos);
    yPos += 5;
  }

  // Add timestamp
  if (finalConfig.includeTimestamp) {
    doc.text(`Generated on: ${format(new Date(), 'yyyy-MM-dd HH:mm:ss')}`, 20, yPos);
    yPos += 10;
  }

  // Add statistics if provided
  if (stats && finalConfig.includeStats) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Member Statistics:', 20, yPos);
    yPos += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const statsText = [
      `Total Members: ${stats.total}`,
      `Active: ${stats.active}`,
      `Pending: ${stats.pending}`,
      `Suspended: ${stats.suspended}`,
      `Disabled: ${stats.disabled}`,
      `Expiring Soon: ${stats.expiring}`
    ];

    statsText.forEach((text, index) => {
      const xPos = 20 + (index % 3) * 80;
      const currentYPos = yPos + Math.floor(index / 3) * 5;
      doc.text(text, xPos, currentYPos);
    });
    yPos += 20;
  }

  // Prepare table data
  const tableData = data.map(member => [
    member.id.toString(),
    `${member.first_name} ${member.last_name}`,
    member.email,
    member.phone,
    member.membership_type || '',
    member.status,
    member.playing_level || '',
    member.jersey_number?.toString() || '',
    member.created_at ? format(new Date(member.created_at), 'yyyy-MM-dd') : ''
  ]);

  // Add table
  autoTable(doc, {
    head: [['ID', 'Name', 'Email', 'Phone', 'Type', 'Status', 'Level', 'Jersey', 'Since']],
    body: tableData,
    startY: yPos,
    styles: {
      fontSize: 8,
      cellPadding: 2
    },
    headStyles: {
      fillColor: [41, 128, 185],
      textColor: [255, 255, 255],
      fontSize: 9,
      fontStyle: 'bold'
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245]
    },
    columnStyles: {
      0: { cellWidth: 15 }, // ID
      1: { cellWidth: 35 }, // Name
      2: { cellWidth: 40 }, // Email
      3: { cellWidth: 25 }, // Phone
      4: { cellWidth: 25 }, // Type
      5: { cellWidth: 20 }, // Status
      6: { cellWidth: 25 }, // Level
      7: { cellWidth: 15 }, // Jersey
      8: { cellWidth: 25 }  // Since
    }
  });

  // Add footer
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 30, doc.internal.pageSize.height - 10);
    doc.text(`${finalConfig.organizationName} - Member Directory`, 20, doc.internal.pageSize.height - 10);
  }

  // Save the file
  doc.save(`${finalConfig.filename}.pdf`);
};

// Word Export
export const exportToWord = async (data: MemberExportData[], stats?: ExportStats, config?: Partial<ExportConfig>) => {
  const finalConfig = { ...defaultConfig, ...config };

  // Create document sections
  const children: any[] = [];

  // Add title
  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: finalConfig.title || 'Member Directory',
          bold: true,
          size: 32
        })
      ],
      alignment: AlignmentType.CENTER,
      spacing: { after: 300 }
    })
  );

  // Add subtitle
  if (finalConfig.subtitle) {
    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: finalConfig.subtitle,
            size: 24
          })
        ],
        alignment: AlignmentType.CENTER,
        spacing: { after: 300 }
      })
    );
  }

  // Add organization info
  if (finalConfig.organizationName) {
    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: finalConfig.organizationName,
            bold: true,
            size: 20
          })
        ],
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 }
      })
    );
  }

  // Add timestamp
  if (finalConfig.includeTimestamp) {
    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: `Generated on: ${format(new Date(), 'yyyy-MM-dd HH:mm:ss')}`,
            size: 18
          })
        ],
        alignment: AlignmentType.CENTER,
        spacing: { after: 400 }
      })
    );
  }

  // Add statistics if provided
  if (stats && finalConfig.includeStats) {
    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: 'Member Statistics',
            bold: true,
            size: 24
          })
        ],
        spacing: { after: 200 }
      })
    );

    const statsTable = new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: [
        new TableRow({
          children: [
            new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: 'Metric', bold: true })] })] }),
            new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: 'Count', bold: true })] })] })
          ]
        }),
        new TableRow({
          children: [
            new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: 'Total Members' })] })] }),
            new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: stats.total.toString() })] })] })
          ]
        }),
        new TableRow({
          children: [
            new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: 'Active Members' })] })] }),
            new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: stats.active.toString() })] })] })
          ]
        }),
        new TableRow({
          children: [
            new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: 'Pending Members' })] })] }),
            new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: stats.pending.toString() })] })] })
          ]
        }),
        new TableRow({
          children: [
            new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: 'Suspended Members' })] })] }),
            new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: stats.suspended.toString() })] })] })
          ]
        }),
        new TableRow({
          children: [
            new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: 'Disabled Members' })] })] }),
            new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: stats.disabled.toString() })] })] })
          ]
        }),
        new TableRow({
          children: [
            new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: 'Expiring Soon' })] })] }),
            new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: stats.expiring.toString() })] })] })
          ]
        })
      ]
    });

    children.push(statsTable);
    children.push(new Paragraph({ text: '', spacing: { after: 400 } }));
  }

  // Add member data title
  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: 'Member Directory',
          bold: true,
          size: 24
        })
      ],
      spacing: { after: 200 }
    })
  );

  // Create member table
  const memberTableRows = [
    new TableRow({
      children: [
        new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: 'ID', bold: true })] })] }),
        new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: 'Name', bold: true })] })] }),
        new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: 'Email', bold: true })] })] }),
        new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: 'Phone', bold: true })] })] }),
        new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: 'Type', bold: true })] })] }),
        new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: 'Status', bold: true })] })] }),
        new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: 'Level', bold: true })] })] })
      ]
    }),
    ...data.map(member => new TableRow({
      children: [
        new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: member.id.toString() })] })] }),
        new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: `${member.first_name} ${member.last_name}` })] })] }),
        new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: member.email })] })] }),
        new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: member.phone })] })] }),
        new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: member.membership_type || '' })] })] }),
        new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: member.status })] })] }),
        new TableCell({ children: [new Paragraph({ children: [new TextRun({ text: member.playing_level || '' })] })] })
      ]
    }))
  ];

  const memberTable = new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: memberTableRows
  });

  children.push(memberTable);

  // Create document
  const doc = new Document({
    sections: [{
      children: children
    }]
  });

  // Generate and download
  const buffer = await Packer.toBlob(doc);
  saveAs(buffer, `${finalConfig.filename}.docx`);
};

// Utility function to prepare member data for export
export const prepareMemberDataForExport = (members: any[]): MemberExportData[] => {
  return members.map(member => ({
    id: member.id,
    first_name: member.first_name,
    last_name: member.last_name,
    email: member.email,
    phone: member.phone,
    membership_type: member.member_type?.name || member.membership_type,
    status: member.status,
    created_at: member.created_at,
    playing_level: typeof member.playing_level === 'object' ? member.playing_level?.name : member.playing_level,
    jersey_number: member.jersey_number,
    dob: member.dob,
    nationality: member.nationality,
    residential_address: member.residential_address,
    occupation: member.occupation,
    emergency_contact_name: member.emergency_contact_name,
    emergency_contact_phone: member.emergency_contact_phone
  }));
}; 